import type {
  Alert, Anomaly, Availability, Forecast, GlobalHistoryPoint, GlobalStatus,
  LiveDetail, LoginResponse, MailCheck, MailConfig, Series, Target, TargetDetail,
  TestMailResult, TimeRange, UserSummary,
} from './types'

const TOKEN_KEY = 'tmmon.token'
const USER_KEY = 'tmmon.user'

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
  }
}

export const auth = {
  token: () => localStorage.getItem(TOKEN_KEY),
  user: () => {
    const raw = localStorage.getItem(USER_KEY)
    return raw ? (JSON.parse(raw) as LoginResponse) : null
  },
  save(response: LoginResponse) {
    localStorage.setItem(TOKEN_KEY, response.token)
    localStorage.setItem(USER_KEY, JSON.stringify(response))
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
  },
  /** True when a token exists and has not expired by its own claim. */
  isValid(): boolean {
    const user = auth.user()
    if (!user || !auth.token()) return false
    return new Date(user.expiresAt).getTime() > Date.now()
  },
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = auth.token()
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  if (init.body) headers.set('Content-Type', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const response = await fetch(path, { ...init, headers })

  if (response.status === 401) {
    // The token expired or was revoked. Drop it and let the router bounce the
    // user to the login screen rather than rendering an empty dashboard.
    auth.clear()
    window.dispatchEvent(new CustomEvent('tmmon:unauthorized'))
    throw new ApiError(401, 'Session expired — please sign in again')
  }

  if (!response.ok) {
    let message = `Request failed (${response.status})`
    try {
      const body = await response.json()
      if (body?.error) message = body.error
    } catch {
      // A non-JSON error body is not worth surfacing verbatim.
    }
    throw new ApiError(response.status, message)
  }

  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

function query(params: Record<string, string | number | string[] | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue
    if (Array.isArray(value)) value.forEach((v) => search.append(key, v))
    else search.set(key, String(value))
  }
  const s = search.toString()
  return s ? `?${s}` : ''
}

export const api = {
  async login(username: string, password: string): Promise<LoginResponse> {
    const response = await request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    })
    auth.save(response)
    return response
  },

  status: () => request<GlobalStatus>('/api/status'),

  statusHistory: (range: TimeRange) =>
    request<GlobalHistoryPoint[]>(`/api/status/history${query({ range })}`),

  targets: () => request<Target[]>('/api/targets'),

  target: (id: string) => request<TargetDetail>(`/api/targets/${encodeURIComponent(id)}`),

  availability: (id: string, range: TimeRange) =>
    request<Availability>(`/api/targets/${encodeURIComponent(id)}/availability${query({ range })}`),

  live: (id: string, mode: string) =>
    request<LiveDetail>(`/api/targets/${encodeURIComponent(id)}/live${query({ mode })}`),

  series: (target: string, keys: string[], range: TimeRange, label?: string) =>
    request<Series[]>(`/api/series${query({ target, key: keys, range, label })}`),

  alerts: () => request<Alert[]>('/api/alerts'),

  alertHistory: (range: TimeRange) =>
    request<Alert[]>(`/api/alerts/history${query({ range })}`),

  forecasts: () => request<Forecast[]>('/api/forecasts'),

  anomalies: (range: TimeRange) => request<Anomaly[]>(`/api/anomalies${query({ range })}`),

  mailConfig: () => request<MailConfig>('/api/admin/mail/config'),

  mailCheck: () => request<MailCheck>('/api/admin/mail/check'),

  sendTestMail: (to: string) =>
    request<TestMailResult>(`/api/admin/mail/test${query({ to })}`, { method: 'POST' }),

  users: () => request<UserSummary[]>('/api/admin/users'),

  createUser: (body: {
    username: string; password: string
    displayName?: string; email?: string; role: string
  }) => request<UserSummary>('/api/admin/users', { method: 'POST', body: JSON.stringify(body) }),

  updateUser: (username: string, body: {
    displayName?: string; email?: string; role?: string; enabled?: boolean
  }) => request<UserSummary>(`/api/admin/users/${encodeURIComponent(username)}`,
    { method: 'PATCH', body: JSON.stringify(body) }),

  setUserPassword: (username: string, newPassword: string) =>
    request<{ updated: boolean }>(
      `/api/admin/users/${encodeURIComponent(username)}/password`,
      { method: 'POST', body: JSON.stringify({ newPassword }) }),

  deleteUser: (username: string) =>
    request<{ deleted: boolean }>(`/api/admin/users/${encodeURIComponent(username)}`,
      { method: 'DELETE' }),

  changeOwnPassword: (currentPassword: string, newPassword: string) =>
    request<{ updated: boolean }>('/api/auth/password',
      { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }),

  preferences: () => request<Record<string, unknown>>('/api/preferences'),

  savePreferences: (prefs: Record<string, unknown>) =>
    request<Record<string, unknown>>('/api/preferences', {
      method: 'PUT',
      body: JSON.stringify(prefs),
    }),
}
